export const PLAYOFF_BEST_OF_VALUES = [3, 5, 7] as const;

export type PlayoffBestOf = (typeof PLAYOFF_BEST_OF_VALUES)[number];

export const PLAYOFF_SERIES_STATUSES = [
  "scheduled",
  "in_progress",
  "clinched",
] as const;

export type PlayoffSeriesStatus = (typeof PLAYOFF_SERIES_STATUSES)[number];
export type PlayoffSide = "us" | "them";
export type PlayoffGameAvailability =
  "required" | "if_necessary" | "not_needed";

export interface PlayoffSeriesState {
  bestOf: PlayoffBestOf;
  ourWins: number;
  opponentWins: number;
  winner?: PlayoffSide | null;
}

export interface PlayoffGameResult {
  forfeitWinner?: PlayoffSide;
  state?: {
    ourRunsByInning?: number[];
    opponentRunsByInning?: number[];
  };
}

export function isPlayoffBestOf(value: unknown): value is PlayoffBestOf {
  return PLAYOFF_BEST_OF_VALUES.some((bestOf) => bestOf === value);
}

export function isPlayoffSeriesStatus(
  value: unknown,
): value is PlayoffSeriesStatus {
  return PLAYOFF_SERIES_STATUSES.some((status) => status === value);
}

export function winsRequired(bestOf: PlayoffBestOf): number {
  return Math.floor(bestOf / 2) + 1;
}

export function playoffSeriesWinner(
  series: PlayoffSeriesState,
): PlayoffSide | null {
  const required = winsRequired(series.bestOf);
  if (series.ourWins >= required) return "us";
  if (series.opponentWins >= required) return "them";
  return null;
}

export function playoffGameAvailability(
  series: PlayoffSeriesState,
  gameNumber: number,
): PlayoffGameAvailability {
  if (series.winner) return "not_needed";
  const guaranteedThrough =
    series.ourWins +
    series.opponentWins +
    winsRequired(series.bestOf) -
    Math.max(series.ourWins, series.opponentWins);
  return gameNumber <= guaranteedThrough ? "required" : "if_necessary";
}

export function playoffResultWinner(
  game: PlayoffGameResult,
): PlayoffSide | null {
  if (game.forfeitWinner === "us" || game.forfeitWinner === "them") {
    return game.forfeitWinner;
  }
  const ourRuns = (game.state?.ourRunsByInning ?? []).reduce(
    (sum, runs) => sum + runs,
    0,
  );
  const opponentRuns = (game.state?.opponentRunsByInning ?? []).reduce(
    (sum, runs) => sum + runs,
    0,
  );
  if (ourRuns === opponentRuns) return null;
  return ourRuns > opponentRuns ? "us" : "them";
}

export function nextSeriesResult(
  series: PlayoffSeriesState,
  gameWinner: PlayoffSide,
): {
  ourWins: number;
  opponentWins: number;
  winner: PlayoffSide | null;
  status: Extract<PlayoffSeriesStatus, "in_progress" | "clinched">;
} {
  const ourWins = series.ourWins + (gameWinner === "us" ? 1 : 0);
  const opponentWins = series.opponentWins + (gameWinner === "them" ? 1 : 0);
  const winner = playoffSeriesWinner({ ...series, ourWins, opponentWins });
  return {
    ourWins,
    opponentWins,
    winner,
    status: winner ? "clinched" : "in_progress",
  };
}
